import { Search, X } from 'lucide-react'
import { cn } from '@/utils/cn'

type EngineListSearchProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  /** When filtering, show “N of M”. */
  matchCount?: number
  totalCount?: number
  matchLabel?: string
  className?: string
  'aria-label'?: string
}

/** Shared list filter for Engine Birth / Tag screens. */
export function EngineListSearch({
  value,
  onChange,
  placeholder = 'Search…',
  disabled = false,
  matchCount,
  totalCount,
  matchLabel,
  className,
  'aria-label': ariaLabel,
}: EngineListSearchProps) {
  const filtering = value.trim().length > 0 && typeof totalCount === 'number'

  return (
    <div className={cn('shrink-0 space-y-1.5', className)}>
      <div className="rfid-engine-search relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={2.4}
          aria-hidden
        />
        <input
          type="search"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          data-no-wedge="true"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="rfid-search !mb-0 w-full !py-2.5 !pl-10 !pr-10 font-mono text-[13px]"
          onChange={(e) => onChange(e.target.value)}
        />
        {value.trim() ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
            onClick={() => onChange('')}
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        ) : null}
      </div>
      {filtering ? (
        <div className="px-0.5 text-[11px] font-semibold text-muted-foreground">
          {matchLabel ??
            `Showing ${matchCount ?? 0} of ${totalCount}`}
        </div>
      ) : null}
    </div>
  )
}

/** Normalize RFID/SKU strings for fuzzy list filtering. */
export function engineSearchNorm(value: string): string {
  return value.trim().toLowerCase().replace(/[\s\-_]+/g, '')
}

export function engineSearchMatch(haystack: string, query: string): boolean {
  const q = engineSearchNorm(query)
  if (!q) return true
  return engineSearchNorm(haystack).includes(q)
}
